import React, { useContext, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  getSimProgress,
  SimProgress,
} from '@yaak/components/services/api/api'
import { ToastContext, ToastContextType } from '@yaak/components/context/toastContext'
import ProgressBar from '@yaak/components/src/ProgressBar'
import Typography from '@yaak/components/src/Typography'
import {
  TypographySizes,
  TypographyStyles,
  TypographyTypes,
} from '@yaak/components/src/Typography/Typography'
import Chip from '@yaak/components/src/Chip'
import Badge, { BadgeType } from '@yaak/components/src/Badge/Badge'
import Empty from '@yaak/components/src/Empty'
import Divider from '@yaak/components/src/Divider'
import Icon from '@yaak/components/src/Icon'
import {
  countSimProgressLessonStatuses,
  filterSimProgressLessons,
  formatSimProgressRatingCategory,
  getSimProgressLessonCategory,
  getSimProgressLessonStatus,
  getSimProgressRatingEntries,
  getSimProgressRatingTotal,
  SIM_PROGRESS_LESSON_STATUSES,
  SIM_PROGRESS_STATUS_FILTERS,
  SimProgressLessonStatus,
  SimProgressStatusFilter,
  sortSimProgressLessons,
} from './simulationProgressHelpers'
import style from './style.less'

interface SimulationProgressProps {
  token: string
  userId?: string
  partnerId?: string
}

const statusColorClassName: Record<SimProgressLessonStatus, string> = {
  in_progress: style.simulationProgressStatusInProgress,
  completed: style.simulationProgressStatusCompleted,
  failed: style.simulationProgressStatusFailed,
  open: style.simulationProgressStatusOpen,
  locked: style.simulationProgressStatusLocked,
}

const statusBadgeType: Record<SimProgressLessonStatus, string> = {
  in_progress: BadgeType.blue,
  completed: BadgeType.green,
  failed: BadgeType.red,
  open: BadgeType.grey,
  locked: BadgeType.grey,
}

const SimulationProgress: React.FunctionComponent<SimulationProgressProps> = ({
  token,
  userId,
  partnerId,
}) => {
  const { setShowToast } = useContext(ToastContext) as ToastContextType
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [progress, setProgress] = useState<SimProgress | null>(null)
  const [statusFilter, setStatusFilter] = useState<SimProgressStatusFilter>('all')
  const [expandedLessonIds, setExpandedLessonIds] = useState<Set<string>>(
    new Set()
  )

  useEffect(() => {
    let cancelled = false

    const fetchProgress = async () => {
      if (!token || !userId) {
        setLoading(false)
        return
      }

      setLoading(true)
      const result = await getSimProgress({
        token,
        userId,
        partnerId,
        onAlert: setShowToast,
      })

      if (!cancelled) {
        setProgress(result || null)
        setLoading(false)
      }
    }

    fetchProgress()

    return () => {
      cancelled = true
    }
  }, [token, userId, partnerId, setShowToast])

  const lessons = useMemo(
    () => filterSimProgressLessons(progress?.lessons || [], statusFilter),
    [progress, statusFilter]
  )

  const orderedLessons = useMemo(
    () => sortSimProgressLessons(progress?.lessons || []),
    [progress]
  )
  const statusCounts = useMemo(
    () => countSimProgressLessonStatuses(progress?.lessons || []),
    [progress]
  )

  const overallErrors = useMemo(
    () => getSimProgressRatingEntries(progress?.rating || null),
    [progress]
  )
  const maxOverallErrors = Math.max(
    0,
    ...overallErrors.map((entry) => entry.count)
  )

  const getStatusLabel = (status: SimProgressLessonStatus) =>
    intl.formatMessage({ id: `simulation.progress.status.${status}` })

  const getRatingLabel = (category: string) => {
    const id = `simulation.progress.rating.${category}`
    return intl.messages[id]
      ? intl.formatMessage({ id })
      : formatSimProgressRatingCategory(category)
  }

  const toggleLesson = (lessonId: string) =>
    setExpandedLessonIds((current) => {
      const next = new Set(current)
      if (next.has(lessonId)) {
        next.delete(lessonId)
      } else {
        next.add(lessonId)
      }
      return next
    })

  return (
    <div className={style.simulationProgress}>
      <div className={style.simulationProgressHeader}>
        <Typography type={TypographyTypes.headline}>
          <FormattedMessage id="simulation.progress.title" />
        </Typography>
      </div>
      <Divider />
      {loading ? (
        <ProgressBar />
      ) : !progress || progress.lessons.length === 0 ? (
        <Empty
          header={<FormattedMessage id="simulation.progress.empty" />}
          content={<FormattedMessage id="simulation.progress.empty_content" />}
        />
      ) : (
        <>
          <div className={style.simulationProgressOverview}>
            <div className={style.simulationProgressSummary}>
              <div className={style.simulationProgressSummaryMain}>
                <Typography
                  type={TypographyTypes.display}
                  styles={TypographyStyles.bold}
                  className={style.simulationProgressPercent}
                >
                  {progress.percent}%
                </Typography>
                <div className={style.simulationProgressSummaryText}>
                  <Typography type={TypographyTypes.body} size={TypographySizes.large}>
                    <FormattedMessage
                      id="simulation.progress.lessons_completed"
                      values={{
                        completed: progress.completedCount,
                        total: progress.totalCount,
                      }}
                    />
                  </Typography>
                  {progress.lastCompletedLesson ? (
                    <Typography
                      type={TypographyTypes.body}
                      size={TypographySizes.small}
                      className={style.simulationProgressLastCompleted}
                    >
                      <FormattedMessage
                        id="simulation.progress.last_completed"
                        values={{ title: progress.lastCompletedLesson.title }}
                      />
                    </Typography>
                  ) : (
                    <Typography
                      type={TypographyTypes.body}
                      size={TypographySizes.small}
                      className={style.simulationProgressLastCompleted}
                    >
                      <FormattedMessage id="simulation.progress.no_last_completed" />
                    </Typography>
                  )}
                </div>
              </div>
              <div
                className={style.simulationProgressStrip}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress.percent}
              >
                {orderedLessons.map((lesson) => {
                  const status = getSimProgressLessonStatus(lesson)
                  return (
                    <div
                      key={lesson.id}
                      className={classNames(
                        style.simulationProgressSegment,
                        statusColorClassName[status]
                      )}
                      title={`${lesson.title}: ${getStatusLabel(status)}`}
                    />
                  )
                })}
              </div>
              <ul className={style.simulationProgressMeterList}>
                {SIM_PROGRESS_LESSON_STATUSES.map((status) => (
                  <li key={status} className={style.simulationProgressMeterRow}>
                    <div className={style.simulationProgressStatusLabel}>
                      <span
                        className={classNames(
                          style.simulationProgressStatusDot,
                          statusColorClassName[status]
                        )}
                      />
                      <Typography type={TypographyTypes.body}>
                        {getStatusLabel(status)}
                      </Typography>
                    </div>
                    <div className={style.simulationProgressMeterTrack}>
                      <div
                        className={classNames(
                          style.simulationProgressMeterFill,
                          statusColorClassName[status]
                        )}
                        style={{
                          width: `${(statusCounts[status] / progress.totalCount) * 100}%`,
                        }}
                      />
                    </div>
                    <Typography
                      type={TypographyTypes.body}
                      styles={TypographyStyles.bold}
                      className={style.simulationProgressMeterCount}
                    >
                      {statusCounts[status]}
                    </Typography>
                  </li>
                ))}
              </ul>
            </div>
            {overallErrors.length > 0 && (
              <div className={style.simulationProgressErrors}>
                <div className={style.simulationProgressSummaryText}>
                  <Typography type={TypographyTypes.title}>
                    <FormattedMessage id="simulation.progress.errors.title" />
                  </Typography>
                  <Typography
                    type={TypographyTypes.body}
                    size={TypographySizes.small}
                    className={style.simulationProgressLastCompleted}
                  >
                    <FormattedMessage id="simulation.progress.errors.subtitle" />
                  </Typography>
                </div>
                <ul className={style.simulationProgressMeterList}>
                  {overallErrors.map(({ category, count }) => (
                    <li
                      key={category}
                      className={style.simulationProgressMeterRow}
                    >
                      <Typography type={TypographyTypes.body}>
                        {getRatingLabel(category)}
                      </Typography>
                      <div className={style.simulationProgressMeterTrack}>
                        <div
                          className={classNames(
                            style.simulationProgressMeterFill,
                            style.simulationProgressErrorFill
                          )}
                          style={{
                            width: `${
                              maxOverallErrors
                                ? (count / maxOverallErrors) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                      <Typography
                        type={TypographyTypes.body}
                        styles={TypographyStyles.bold}
                        className={style.simulationProgressMeterCount}
                      >
                        {count}
                      </Typography>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className={style.simulationProgressFilters}>
            {SIM_PROGRESS_STATUS_FILTERS.map((filter) => (
              <Chip
                key={filter}
                selected={statusFilter === filter}
                text={
                  <FormattedMessage
                    id={
                      filter === 'all'
                        ? 'simulation.progress.filter.all'
                        : `simulation.progress.status.${filter}`
                    }
                  />
                }
                onClick={() => setStatusFilter(filter)}
              />
            ))}
          </div>
          {lessons.length === 0 ? (
            <Empty
              header={<FormattedMessage id="simulation.progress.no_filter_results" />}
              content={
                <FormattedMessage id="simulation.progress.no_filter_results_content" />
              }
            />
          ) : (
            <table className={style.simulationProgressTable}>
              <thead>
                <tr>
                  <th>
                    <FormattedMessage id="simulation.progress.header.lesson" />
                  </th>
                  <th>
                    <FormattedMessage id="simulation.progress.header.status" />
                  </th>
                  <th>
                    <FormattedMessage id="simulation.progress.header.category" />
                  </th>
                  <th>
                    <FormattedMessage id="simulation.progress.header.errors" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {lessons.map((lesson) => {
                  const status = getSimProgressLessonStatus(lesson)
                  const errorTotal = getSimProgressRatingTotal(lesson.rating)
                  const expanded = expandedLessonIds.has(lesson.id)
                  return (
                    <React.Fragment key={lesson.id}>
                      <tr>
                        <td>
                          {lesson.rating ? (
                            <button
                              type="button"
                              className={style.simulationProgressLessonToggle}
                              aria-expanded={expanded}
                              aria-label={intl.formatMessage(
                                { id: 'simulation.progress.errors.toggle' },
                                { title: lesson.title }
                              )}
                              onClick={() => toggleLesson(lesson.id)}
                            >
                              <Icon
                                name={expanded ? 'ExpandLess' : 'ExpandMore'}
                              />
                              <Typography
                                type={TypographyTypes.body}
                                size={TypographySizes.large}
                                styles={TypographyStyles.bold}
                              >
                                {lesson.title}
                              </Typography>
                            </button>
                          ) : (
                            <Typography
                              type={TypographyTypes.body}
                              size={TypographySizes.large}
                              styles={TypographyStyles.bold}
                              className={style.simulationProgressLessonTitle}
                            >
                              {lesson.title}
                            </Typography>
                          )}
                        </td>
                        <td>
                          <Badge
                            type={statusBadgeType[status]}
                            withDot={status === 'in_progress'}
                            label={getStatusLabel(status)}
                          />
                        </td>
                        <td>
                          <Typography type={TypographyTypes.body}>
                            {getSimProgressLessonCategory(lesson)}
                          </Typography>
                        </td>
                        <td>
                          <Typography type={TypographyTypes.body}>
                            {errorTotal ?? '—'}
                          </Typography>
                        </td>
                      </tr>
                      {expanded && lesson.rating && (
                        <tr className={style.simulationProgressLessonErrors}>
                          <td colSpan={4}>
                            <Typography
                              type={TypographyTypes.body}
                              size={TypographySizes.small}
                              className={style.simulationProgressLastCompleted}
                            >
                              <FormattedMessage id="simulation.progress.errors.lesson_title" />
                            </Typography>
                            <ul
                              className={
                                style.simulationProgressLessonErrorList
                              }
                            >
                              {getSimProgressRatingEntries(lesson.rating).map(
                                ({ category, count }) => (
                                  <li key={category}>
                                    <Typography type={TypographyTypes.body}>
                                      {getRatingLabel(category)}
                                    </Typography>
                                    <Typography
                                      type={TypographyTypes.body}
                                      styles={TypographyStyles.bold}
                                    >
                                      {count}
                                    </Typography>
                                  </li>
                                )
                              )}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  )
}

export default SimulationProgress
